import { useEffect, useState } from "react";
import { api, type HrZonesResponse } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";
import { fmtMins } from "./_shared";

export default function HeartZones() {
  const [data, setData] = useState<HrZonesResponse | null | "error">(null);

  useEffect(() => {
    api.heart
      .zones(7)
      .then(setData)
      .catch(() => setData("error"));
  }, []);

  const d = data && data !== "error" ? data : null;
  const hasData = d != null && d.period_total_min > 0;

  // Max zone at top (like Garmin). Scale bars to the busiest zone.
  const zones = d ? [...d.zones].reverse() : [];
  const maxMin = Math.max(1, ...zones.map((z) => z.period_min));

  return (
    <div className="animate-fade-in">
      <h2 className="section-title mb-1">Time in zones</h2>
      <p className="text-ink-subtle text-small mb-4">
        Last 7 days · zones are % of your max HR{d ? ` (~${d.max_hr} bpm, age ${d.age})` : ""}.
      </p>

      {!hasData ? (
        <EmptyState error={data === "error"} title="Not enough heart-rate data yet">
          As your watch streams more heart rate, we split your time into training zones — warm-up
          through max effort.
        </EmptyState>
      ) : (
        <div className="card card-pad space-y-3">
          {zones.map((z) => (
            <div key={z.name}>
              <div className="flex items-baseline justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: z.color }} />
                  <span className="text-ink">{z.name}</span>
                  <span className="text-ink-subtle text-xs">{z.lo_bpm}–{z.hi_bpm} bpm</span>
                </span>
                <span className="tabular-nums text-ink-muted">
                  {fmtMins(z.period_min)}
                  {z.today_min > 0 && <span className="text-ink-subtle"> · {fmtMins(z.today_min)} today</span>}
                </span>
              </div>
              <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-ink-line/40">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${(z.period_min / maxMin) * 100}%`, backgroundColor: z.color }}
                />
              </div>
            </div>
          ))}
          <p className="pt-1 text-xs text-ink-subtle">
            {fmtMins(d!.period_total_min)} of heart-rate data over the last {d!.period_days} days.
            Higher zones mean harder effort — most days sit in Rest/Warm-up, with spikes during
            activity.
          </p>
        </div>
      )}
    </div>
  );
}
